import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BookOpenCheck, Loader2, Save } from "lucide-react";
import { API, authHeaders } from "@/constants/roles";

// Editable per-role rules & guidelines. Workers see their role's content on their dashboard.
export const GuidelinesCard = () => {
  const [guidelines, setGuidelines] = useState(null);
  const [tab, setTab] = useState(null);
  const [draft, setDraft] = useState("");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/guidelines`, { headers: authHeaders() })
      .then((r) => {
        setGuidelines(r.data);
        if (!tab && r.data.length) {
          setTab(r.data[0].role);
          setDraft(r.data[0].content || "");
        }
      })
      .catch(() => toast.error("Could not load guidelines."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(load, [load]);

  const pick = (role) => {
    setTab(role);
    setDraft((guidelines.find((g) => g.role === role) || {}).content || "");
    setDirty(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/guidelines/${encodeURIComponent(tab)}`, { content: draft }, { headers: authHeaders() });
      setGuidelines((gs) => gs.map((g) => (g.role === tab ? { ...g, content: draft } : g)));
      setDirty(false);
      toast.success(`${tab} guidelines saved — live on worker dashboards now.`);
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not save guidelines.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="guidelines-card" className="mt-6 rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-6 sm:p-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <BookOpenCheck className="h-5 w-5 text-cyan-400" />
          <h2 className="font-display text-lg font-bold text-[var(--pv-text)]">Role guidelines</h2>
        </div>
        {dirty && (
          <button data-testid="guidelines-save-btn" onClick={save} disabled={saving} className="flex items-center gap-1.5 rounded-full bg-cyan-500 px-4 py-2 text-xs font-bold text-white hover:bg-cyan-600 disabled:opacity-60">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save guidelines
          </button>
        )}
      </div>
      <p className="mt-2 text-xs text-[var(--pv-text-4)]">
        The rules and quality bar for each role. Workers see their role's guidelines pinned on their dashboard.
      </p>
      {!guidelines ? (
        <p data-testid="guidelines-loading" className="py-8 text-center text-sm text-[var(--pv-text-4)]">Loading guidelines...</p>
      ) : (
        <>
          <div className="mt-4 flex flex-wrap gap-1.5">
            {guidelines.map((g) => (
              <button
                key={g.role}
                data-testid={`guidelines-tab-${g.role.toLowerCase().replace(/[\s/]+/g, "-")}`}
                onClick={() => pick(g.role)}
                className={`rounded-full px-3.5 py-1.5 text-[11px] font-bold transition-colors ${tab === g.role ? "bg-cyan-500 text-white" : "border border-[var(--pv-border)] bg-[var(--pv-subtle)] text-[var(--pv-text-3)]"}`}
              >
                {g.role}{g.content ? "" : " ·"}
              </button>
            ))}
          </div>
          <textarea
            data-testid="guidelines-editor"
            rows={8}
            value={draft}
            onChange={(e) => { setDraft(e.target.value); setDirty(true); }}
            placeholder={`Write the ${tab} guidelines — quality bar, delivery rules, dos and don'ts…`}
            className="mt-4 w-full rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-bg)] px-4 py-3 text-sm font-medium text-[var(--pv-text)] placeholder:text-[var(--pv-text-4)] focus:border-cyan-400 focus:outline-none focus:ring-4 focus:ring-cyan-500/10"
          />
        </>
      )}
    </div>
  );
};
